const C = {
  bg: "#0B0E14",
  text: "#E8EBF0",
  muted: "#8B93A3",
  dim: "#5A6272",
  green: "#2BD97C",
  amber: "#E3A44A",
  red: "#C75E5E",
};

const VERDICT_COLOR = { Late: C.red, "Well positioned": C.amber, Early: C.green };

const mono = (px, w = 400) => `${w} ${px}px "JetBrains Mono", "Courier New", monospace`;

function wrapText(ctx, text, x, y, maxW, lineH) {
  const words = String(text).split(" ");
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, y);
      y += lineH;
      line = w;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, y);
  return y + lineH;
}

export function drawVerdictCard(result, mode = "stock") {
  const W = 1200;
  const H = 630;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const x = c.getContext("2d");
  x.fillStyle = C.bg;
  x.fillRect(0, 0, W, H);
  x.strokeStyle = "rgba(255,255,255,0.1)";
  x.lineWidth = 2;
  x.strokeRect(1, 1, W - 2, H - 2);
  x.fillStyle = C.green;
  x.fillRect(0, 0, W, 6);

  x.fillStyle = C.green;
  x.font = mono(22, 700);
  x.fillText("✳ FUNDPRINTS · THE CHECK", 64, 92);

  const sym = result.symbol;
  x.fillStyle = C.text;
  x.font = mono(46, 700);
  x.fillText(mode === "fund" ? `How crowded is ${sym}?` : `Is it too late to buy ${sym}?`, 64, 178);

  if (mode === "fund") {
    const late = result.late_pct || 0;
    x.fillStyle = late >= 50 ? C.red : late >= 25 ? C.amber : C.green;
    x.font = mono(72, 700);
    x.fillText(`${late}% in late-stage names`, 64, 288);
    x.fillStyle = C.muted;
    x.font = mono(26);
    const breakdown = Object.entries(result.stage_pct || {})
      .map(([st, w]) => `${w}% ${st}`)
      .join(" · ");
    wrapText(x, `BY WEIGHT: ${breakdown.toUpperCase()}`, 64, 356, W - 128, 40);
    if (result.crowded_names?.length) {
      x.fillStyle = C.amber;
      x.font = mono(24);
      const crowded = result.crowded_names.slice(0, 5).map((n) => `${n.symbol} ${n.weight}%`).join(" · ");
      wrapText(x, `The crowded weight: ${crowded}`, 64, 442, W - 128, 36);
    }
  } else {
    x.fillStyle = C[result.verdict_tone] || VERDICT_COLOR[result.verdict] || C.amber;
    let byy;
    if ((result.verdict || "").length > 16) {
      x.font = mono(50, 700);
      byy = wrapText(x, result.verdict || "", 64, 262, W - 128, 60) + 4;
    } else {
      x.font = mono(96, 700);
      x.fillText(result.verdict || "", 64, 306);
      byy = 372;
    }
    x.fillStyle = C.muted;
    x.font = mono(26);
    const bits = [result.stage, result.tape ? `TAPE ${result.tape}` : null,
      result.n_specialists != null ? `${result.n_specialists} PROVEN SPECIALISTS` : null]
      .filter(Boolean).join(" · ");
    x.fillText(bits.toUpperCase(), 64, byy);
    byy += 52;
    if (result.n_exits > 0) {
      x.fillStyle = C.red;
      x.font = mono(24, 700);
      x.fillText(`${result.n_exits} SPECIALIST${result.n_exits === 1 ? "" : "S"} LEFT THIS QUARTER`, 64, byy);
      byy += 44;
    }
    if (result.meaning_line) {
      x.fillStyle = C.text;
      x.font = mono(26);
      byy = wrapText(x, result.meaning_line, 64, byy, W - 128, 40) + 6;
    }
    if (result.base_rate_line) {
      x.fillStyle = C.amber;
      x.font = mono(24);
      wrapText(x, result.base_rate_line, 64, byy, W - 128, 36);
    }
  }

  x.fillStyle = C.dim;
  x.font = mono(22);
  x.fillText(
    `fundprints.com/${mode === "fund" ? "how-crowded-is" : "is-it-too-late"}/${sym}`,
    64, H - 56
  );
  if (result.as_of) {
    x.textAlign = "right";
    x.fillText(`EVIDENCE AS OF ${result.as_of}`, W - 64, H - 56);
    x.textAlign = "left";
  }
  return c;
}

export async function saveVerdictCard(result, mode = "stock") {
  const canvas = drawVerdictCard(result, mode);
  return exportCanvas(canvas, `fundprints-${result.symbol}.png`);
}

export function draw401kCard(res) {
  const W = 1200;
  const H = 630;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const x = c.getContext("2d");
  x.fillStyle = C.bg;
  x.fillRect(0, 0, W, H);
  x.strokeStyle = "rgba(255,255,255,0.1)";
  x.lineWidth = 2;
  x.strokeRect(1, 1, W - 2, H - 2);
  x.fillStyle = C.green;
  x.fillRect(0, 0, W, 6);

  x.fillStyle = C.green;
  x.font = mono(22, 700);
  x.fillText("✳ FUNDPRINTS · THE 401K CHECK", 64, 92);

  x.fillStyle = C.text;
  x.font = mono(46, 700);
  x.fillText("How crowded is my 401k?", 64, 178);

  const active = res.active_late_pct;
  const useActive = active != null && (res.market_pct + res.uncovered_pct) >= 15 && Math.abs(active - (res.late_pct || 0)) >= 5;
  const late = useActive ? active : res.late_pct || 0;
  x.fillStyle = late >= 40 ? C.red : late >= 20 ? C.amber : C.green;
  x.font = mono(useActive ? 56 : 72, 700);
  x.fillText(useActive ? `${late}% of my picks are late-stage` : `${late}% in late-stage names`, 64, 288);

  const STAGE_ORDER = ["Emerging", "Early Conviction", "Consensus", "Crowded", "Fading"];
  const bits = STAGE_ORDER.filter((st) => res.stage_pct?.[st]).map((st) => `${res.stage_pct[st]}% ${st}`);
  if (res.market_pct > 0) bits.push(`${res.market_pct}% the market itself`);
  if (res.uncovered_pct > 0) bits.push(`${res.uncovered_pct}% not readable`);
  x.fillStyle = C.muted;
  x.font = mono(26);
  let y = wrapText(x, bits.join(" · ").toUpperCase(), 64, 356, W - 128, 40);
  if (res.crowded_names?.length) {
    x.fillStyle = C.amber;
    x.font = mono(24);
    wrapText(x, `The crowded weight: ${res.crowded_names.map((n) => `${n.symbol} ${n.pts}%`).join(" · ")}`,
      64, y + 16, W - 128, 36);
  }

  x.fillStyle = C.dim;
  x.font = mono(22);
  x.fillText("fundprints.com/how-crowded-is-my-401k", 64, H - 56);
  if (res.as_of) {
    x.textAlign = "right";
    x.fillText(`EVIDENCE AS OF ${res.as_of}`, W - 64, H - 56);
    x.textAlign = "left";
  }
  return c;
}

export async function save401kCard(res) {
  return exportCanvas(draw401kCard(res), "fundprints-401k.png");
}

async function exportCanvas(canvas, filename) {
  const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
  if (!blob) return false;
  try {
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
    }
  } catch {}
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  return true;
}
